/**
 * Casework API - RTK Query endpoints for the case management module.
 *
 * =====================================================================
 * CRITICAL ARCHITECTURE NOTE FOR FUTURE DEVELOPERS / LLMs:
 * =====================================================================
 *
 * TNN's backend (Django REST Framework) uses a default pagination class
 * called ``StandardPagination`` with page_size=10. This means every
 * list endpoint returns a **paginated envelope**, not a flat array:
 *
 *   { "count": 42, "page": 1, "page_size": 10, "results": [...] }
 *
 * The FE's ``baseApi`` (RTK Query with fetchBaseQuery) has a custom
 * ``responseHandler`` that unwraps the ``{ success: true, data: ... }``
 * envelope the backend wraps around every response. After unwrapping,
 * the RTK Query ``data`` for a list endpoint is:
 *
 *   { "count": 42, "page": 1, "results": [...] }   ← NOT an array!
 *
 * If a ``build.query`` endpoint is typed as ``Type[]`` (array) but
 * doesn't have a ``transformResponse`` to extract ``.results``, then:
 *
 *   - ``data`` is the paginated object (not an array)
 *   - ``.map()`` / ``.filter()`` / ``.reduce()`` on it either:
 *     a) Crash with "TypeError: x.map is not a function" (the
 *        Accounting page crash David reported), or
 *     b) Silently produce nothing (the Resource Directory showing
 *        perpetual "Loading..." with no error).
 *
 * THE FIX: every ``build.query<SomeType[], ...>`` endpoint that hits
 * a DRF paginated viewset MUST have:
 *
 *   transformResponse: (r: { results?: SomeType[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * This was the root cause of Findings 9, 10, 12 in the v1.6 bug report,
 * plus the Appointments display bug in the v4.0.1 QA report. A full
 * audit in v4.0 confirmed all 20 array-typed queries across 5 API files
 * now have this transform.
 *
 * IF YOU ADD A NEW LIST ENDPOINT: copy the transformResponse pattern
 * from any existing endpoint in this file. If you forget, the page
 * that consumes the data will either crash or show nothing.
 * =====================================================================
 *
 * Endpoints:
 *   GET    /cases/cases/                        list (paginated)
 *   POST   /cases/cases/                        create (idempotency-key required)
 *   GET    /cases/cases/:id/                    retrieve
 *   PATCH  /cases/cases/:id/                    partial update
 *   POST   /cases/cases/:id/close/              close with outcome
 *   GET    /cases/cases/:id/documents/          list documents
 *   POST   /cases/cases/:id/documents/          add document
 *   POST   /cases/documents/:id/upload/         upload file (multipart)
 *   GET    /cases/cases/:id/referrals/          list referrals
 *   POST   /cases/cases/:id/referrals/          add referral
 *   PATCH  /cases/referrals/:id/status/         update status
 *   GET    /cases/referrals/:id/portal-link/    get portal URL
 *   POST   /cases/referrals/:id/send/           email portal link to partner
 *   GET    /cases/cases/:id/services/           list service logs
 *   POST   /cases/cases/:id/services/           log service
 *   GET    /cases/cases/:id/tasks/              list tasks
 *   POST   /cases/cases/:id/tasks/              add task
 *   PATCH  /cases/tasks/:id/                    update task
 *   GET    /cases/tasks/                        cross-case task board
 *   GET    /cases/cases/:id/timeline/           timeline events
 *   GET    /cases/cases/:id/household/          household members
 *   POST   /cases/cases/:id/household/          add member
 *   GET    /cases/referral-orgs/                resource directory
 *   POST   /cases/referral-orgs/                add org
 *   GET    /cases/dashboard/                    dashboard summary
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { Paginated } from "@/shared/api/types";

import type {
  CaseClosureReason,
  CaseDashboard,
  CaseDocument,
  CaseHouseholdMember,
  CaseNote,
  CaseReferral,
  CaseReferralBrief,
  CaseTask,
  CaseTaskAttachment,
  CaseTaskComment,
  CaseTaskActivity,
  AssistanceProgram,
  ReferralOrg,
  ReferralOrgReferredCase,
  ServiceCase,
  ServiceLog,
  TimelineEvent,
} from "../model/types";

/** Paginated nested lists on a case (referrals, tasks, household). */
export type CaseNestedListParams = {
  caseId: number;
  page?: number;
  page_size?: number;
};

const CASE_NESTED_LIST_PAGE_SIZE = 20;

/** Cross-case task board / Tasks page column pages. */
export const BOARD_TASK_PAGE_SIZE = 15;

/** Whether more pages exist for an accumulated task list. */
export function boardTaskListHasMore(
  data: Paginated<CaseTask> | undefined,
  loadedCount: number,
): boolean {
  if (!data) return false;
  if (data.next) return true;
  if (data.total_pages > 0 && data.page < data.total_pages) return true;
  if (data.count > 0) return loadedCount < data.count;
  return false;
}

export type ListAllTasksParams = {
  page?: number;
  page_size?: number;
  status?: string;
  /** Full-text search (title, description, assignee). */
  search?: string;
  assigned_to_id?: number;
  overdue?: "1";
  linked?: "case" | "standalone";
  /** due_date, -due_date, priority, -priority, created_at, -created_at */
  ordering?: string;
  /** Client-only: bust RTK cache when filters return to a prior key (not sent to API). */
  cacheBust?: number;
};

function normalizeBoardTaskList(
  response: unknown,
  arg: ListAllTasksParams,
): Paginated<CaseTask> {
  return normalizeCaseNestedList<CaseTask>(response, {
    caseId: 0,
    page: arg.page,
    page_size: arg.page_size ?? BOARD_TASK_PAGE_SIZE,
  });
}

/**
 * Nested case lists may return a paginated envelope or a flat array (older
 * nested routes). Always normalize to Paginated so UI and providesTags match
 * listExpenses / listCases.
 */
function normalizeCaseNestedList<T>(
  response: unknown,
  arg: CaseNestedListParams,
): Paginated<T> {
  const page = arg.page ?? 1;
  const pageSize = arg.page_size ?? CASE_NESTED_LIST_PAGE_SIZE;

  if (Array.isArray(response)) {
    const count = response.length;
    const start = (page - 1) * pageSize;
    return {
      count,
      page,
      page_size: pageSize,
      total_pages: count === 0 ? 0 : Math.ceil(count / pageSize),
      next: null,
      previous: null,
      results: response.slice(start, start + pageSize) as T[],
    };
  }

  if (
    response &&
    typeof response === "object" &&
    "results" in response &&
    Array.isArray((response as Paginated<T>).results)
  ) {
    const p = response as Paginated<T>;
    const count = p.count ?? p.results.length;
    const size = p.page_size ?? pageSize;
    return {
      count,
      page: p.page ?? page,
      page_size: size,
      total_pages: p.total_pages ?? (count === 0 ? 0 : Math.ceil(count / size)),
      next: p.next ?? null,
      previous: p.previous ?? null,
      results: p.results,
    };
  }

  return {
    count: 0,
    page,
    page_size: pageSize,
    total_pages: 0,
    next: null,
    previous: null,
    results: [],
  };
}

interface CaseListParams {
  page?: number;
  page_size?: number;
  status?: string;
  urgency?: string;
  presenting_need?: string;
  assigned_to_id?: number;
  /** Cases assigned to a staff member (resolves Person.user_id). */
  assigned_to_person?: number;
  /** Cases opened for one member - used by the person profile. */
  primary_client?: number;
  q?: string;
  ordering?: string;
  deadline_within_days?: number;
  /** When true, only cases with an outbound referral. */
  has_referral?: boolean;
}

interface ClosePayload {
  status: "closed_successful" | "closed_unsuccessful" | "closed_ineligible";
  outcome?: string;
  outcome_notes?: string;
  closure_reason?: CaseClosureReason | "";
  closure_summary?: string;
  follow_up_date?: string | null;
}

interface ReferralStatusPatch {
  status?: string;
  outcome_confirmed?: boolean;
  outcome_notes?: string;
}

export interface CampaignAction {
  id: number;
  case: number;
  action_type: string;
  action_type_display: string;
  target: string;
  date: string;
  participant_count: number;
  goal_count: number | null;
  outcome: string;
  notes: string;
  created_at: string;
}

export interface DocumentChecklistItem {
  document_type: string;
  label: string;
  required: boolean;
}

export interface DocumentChecklist {
  id: number;
  name: string;
  vertical: string;
  is_default: boolean;
  item_count: number;
  items: DocumentChecklistItem[];
}

export interface ApplyChecklistResult {
  checklist: string;
  documents_created: number;
  total_documents: number;
}

export const casesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // ── Cases ────────────────────────────────────────────────────────────────
    listCases: build.query<Paginated<ServiceCase>, CaseListParams | void>({
      query: (params) => ({
        url: "/cases/cases/",
        params: (params ?? {}) as Record<string, unknown>,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((c) => ({ type: "ServiceCase" as const, id: c.id })),
              { type: "ServiceCase", id: "LIST" },
            ]
          : [{ type: "ServiceCase", id: "LIST" }],
    }),

    getCase: build.query<ServiceCase, number>({
      query: (id) => `/cases/cases/${id}/`,
      providesTags: (_r, _e, id) => [{ type: "ServiceCase", id }],
    }),

    getCaseReferralBrief: build.query<CaseReferralBrief, number>({
      query: (id) => `/cases/cases/${id}/referral-brief/`,
      providesTags: (_r, _e, id) => [{ type: "ServiceCase", id }],
    }),

    // ---------------------------------------------------------------
    // Case creation - requires an Idempotency-Key header.
    //
    // v4.0.1 fix (QA: "Creating cases returns 400 Idempotency-Key
    // header required"):
    //
    // The backend's ServiceCaseViewSet uses IdempotencyMixin which
    // requires every POST to include an ``Idempotency-Key`` header.
    // This prevents duplicate case creation from double-clicks or
    // network retries - the backend caches the first response for
    // a given key and replays it if the same key arrives again.
    //
    // ``crypto.randomUUID()`` is called inside the ``query`` function,
    // which RTK Query invokes each time the mutation is triggered.
    // This means each user action gets a unique key (correct), but
    // automatic retries also get a new key (acceptable - a retry
    // after a network error where the first request may not have
    // arrived should create the case, not replay a stale response).
    //
    // If we ever need true retry-safe idempotency (same key across
    // retries), generate the UUID in the component and pass it as
    // part of the mutation argument.
    // ---------------------------------------------------------------
    createCase: build.mutation<
      ServiceCase,
      { body: Partial<ServiceCase>; idempotencyKey?: string }
    >({
      query: ({ body, idempotencyKey }) => ({
        url: "/cases/cases/",
        method: "POST",
        body,
        headers: {
          "Idempotency-Key":
            idempotencyKey
            ?? (typeof crypto !== "undefined" && "randomUUID" in crypto
              ? crypto.randomUUID()
              : `case-${Date.now()}-${Math.random().toString(36).slice(2)}`),
        },
      }),
      invalidatesTags: [
        { type: "ServiceCase", id: "LIST" },
        { type: "CaseDashboard", id: "SUMMARY" },
        { type: "Interaction", id: "SUMMARY" },
      ],
    }),

    updateCase: build.mutation<
      ServiceCase,
      { id: number; data: Partial<ServiceCase> & { status_change_note?: string } }
    >({
      query: ({ id, data }) => ({ url: `/cases/cases/${id}/`, method: "PATCH", body: data }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ServiceCase", id },
        { type: "ServiceCase", id: "LIST" },
        { type: "CaseDashboard", id: "SUMMARY" },
        { type: "CaseNote", id },
        { type: "CaseTimeline", id },
      ],
    }),

    closeCase: build.mutation<ServiceCase, { id: number; data: ClosePayload }>({
      query: ({ id, data }) => ({
        url: `/cases/cases/${id}/close/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ServiceCase", id },
        { type: "ServiceCase", id: "LIST" },
        { type: "CaseDashboard", id: "SUMMARY" },
        { type: "Interaction", id: "SUMMARY" },
        { type: "CaseTimeline", id },
      ],
    }),

    listCampaignActions: build.query<CampaignAction[], number>({
      query: (caseId) => `/cases/cases/${caseId}/campaign-actions/`,
      transformResponse: (r: CampaignAction[] | { results?: CampaignAction[] }) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: (_r, _e, caseId) => [
        { type: "ServiceCase" as const, id: `actions-${caseId}` },
      ],
    }),

    addCampaignAction: build.mutation<
      CampaignAction,
      { caseId: number; data: Partial<CampaignAction> }
    >({
      query: ({ caseId, data }) => ({
        url: `/cases/cases/${caseId}/campaign-actions/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "ServiceCase" as const, id: `actions-${caseId}` },
        { type: "ServiceCase" as const, id: caseId },
        { type: "CaseTimeline", id: caseId },
      ],
    }),

    listCaseNotes: build.query<CaseNote[], number>({
      query: (caseId) => `/cases/cases/${caseId}/notes/`,
      transformResponse: (r: CaseNote[] | { results?: CaseNote[] }) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: (_r, _e, caseId) => [{ type: "CaseNote", id: caseId }],
    }),

    addCaseNote: build.mutation<CaseNote, { caseId: number; text: string }>({
      query: ({ caseId, text }) => ({
        url: `/cases/cases/${caseId}/notes/`,
        method: "POST",
        body: { text },
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "CaseNote", id: caseId },
        { type: "CaseTimeline", id: caseId },
      ],
    }),

    // ── Documents ────────────────────────────────────────────────────────────
    listDocuments: build.query<CaseDocument[], number>({
      query: (caseId) => `/cases/cases/${caseId}/documents/`,
      transformResponse: (r: CaseDocument[] | { results?: CaseDocument[] }) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: (_r, _e, caseId) => [{ type: "CaseDocument", id: caseId }],
    }),

    addDocument: build.mutation<CaseDocument, { caseId: number; data: Partial<CaseDocument> }>({
      query: ({ caseId, data }) => ({
        url: `/cases/cases/${caseId}/documents/`,
        method: "POST",
        body: { ...data, case: caseId },
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "CaseDocument", id: caseId },
        { type: "ServiceCase", id: caseId },
        { type: "CaseTimeline", id: caseId },
      ],
    }),

    uploadDocumentFile: build.mutation<CaseDocument, { docId: number; file: File }>({
      query: ({ docId, file }) => {
        const form = new FormData();
        form.append("file", file);
        return { url: `/cases/documents/${docId}/upload/`, method: "POST", body: form };
      },
      invalidatesTags: () => [{ type: "CaseDocument" }],
    }),

    updateDocumentStatus: build.mutation<
      CaseDocument,
      { docId: number; caseId: number; status: string }
    >({
      query: ({ docId, status }) => ({
        url: `/cases/documents/${docId}/`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "CaseDocument", id: caseId },
        { type: "ServiceCase", id: caseId },
        { type: "CaseTimeline", id: caseId },
      ],
    }),

    listDocumentChecklists: build.query<DocumentChecklist[], void>({
      query: () => "/cases/cases/checklists/",
      transformResponse: (r: DocumentChecklist[] | { results?: DocumentChecklist[] }) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "DocumentChecklist", id: "LIST" }],
    }),

    applyDocumentChecklist: build.mutation<
      ApplyChecklistResult,
      { caseId: number; checklistId?: number }
    >({
      query: ({ caseId, checklistId }) => ({
        url: `/cases/cases/${caseId}/apply-checklist/`,
        method: "POST",
        body: checklistId ? { checklist_id: checklistId } : {},
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "CaseDocument", id: caseId },
        { type: "ServiceCase", id: caseId },
        { type: "CaseTimeline", id: caseId },
      ],
    }),

    // ── Referrals ────────────────────────────────────────────────────────────
    listReferrals: build.query<Paginated<CaseReferral>, CaseNestedListParams>({
      query: ({ caseId, page = 1, page_size = CASE_NESTED_LIST_PAGE_SIZE }) => ({
        url: `/cases/cases/${caseId}/referrals/`,
        params: { page, page_size },
      }),
      transformResponse: (response, _meta, arg) =>
        normalizeCaseNestedList<CaseReferral>(response, arg),
      providesTags: (result, _e, { caseId }) =>
        result
          ? [
              ...result.results.map((r) => ({ type: "CaseReferral" as const, id: r.id })),
              { type: "CaseReferral", id: caseId },
            ]
          : [{ type: "CaseReferral", id: caseId }],
    }),

    addReferral: build.mutation<CaseReferral, { caseId: number; data: Partial<CaseReferral> }>({
      query: ({ caseId, data }) => ({
        url: `/cases/cases/${caseId}/referrals/`,
        method: "POST",
        body: { ...data, case: caseId },
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "CaseReferral", id: caseId },
        { type: "ServiceCase", id: caseId },
        { type: "CaseDashboard", id: "SUMMARY" },
        { type: "CaseTimeline", id: caseId },
      ],
    }),

    updateReferralStatus: build.mutation<
      CaseReferral,
      { refId: number; caseId: number; data: ReferralStatusPatch }
    >({
      query: ({ refId, data }) => ({
        url: `/cases/referrals/${refId}/status/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "CaseReferral", id: caseId },
        { type: "CaseTimeline", id: caseId },
      ],
    }),

    getReferralPortalLink: build.query<
      { portal_url: string; case_number: string; external_token?: string },
      number
    >({
      query: (refId) => `/cases/referrals/${refId}/portal-link/`,
    }),

    sendReferralBrief: build.mutation<
      { success: boolean; partner_email: string; partner_email_sent: boolean },
      { refId: number; caseId: number }
    >({
      query: ({ refId }) => ({
        url: `/cases/referrals/${refId}/send/`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "CaseReferral", id: caseId },
        { type: "CaseTimeline", id: caseId },
      ],
    }),

    // ── Services ─────────────────────────────────────────────────────────────
    listServices: build.query<ServiceLog[], number>({
      query: (caseId) => `/cases/cases/${caseId}/services/`,
      transformResponse: (r: ServiceLog[] | { results?: ServiceLog[] }) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: (_r, _e, caseId) => [{ type: "ServiceLog", id: caseId }],
    }),

    addService: build.mutation<ServiceLog, { caseId: number; data: Partial<ServiceLog> & { request_approval?: boolean } }>({
      query: ({ caseId, data }) => ({
        url: `/cases/cases/${caseId}/services/`,
        method: "POST",
        body: { ...data, case: caseId },
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "ServiceLog", id: caseId },
        { type: "CaseTimeline", id: caseId },
        { type: "CaseDashboard", id: "SUMMARY" },
      ],
    }),

    approveService: build.mutation<ServiceLog, { caseId: number; serviceId: number }>({
      query: ({ caseId, serviceId }) => ({
        url: `/cases/cases/${caseId}/services/${serviceId}/approve/`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "ServiceLog", id: caseId },
        { type: "CaseTimeline", id: caseId },
        { type: "CaseDashboard", id: "SUMMARY" },
      ],
    }),

    rejectService: build.mutation<
      ServiceLog,
      { caseId: number; serviceId: number; reason?: string }
    >({
      query: ({ caseId, serviceId, reason }) => ({
        url: `/cases/cases/${caseId}/services/${serviceId}/reject/`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "ServiceLog", id: caseId },
        { type: "CaseTimeline", id: caseId },
        { type: "CaseDashboard", id: "SUMMARY" },
      ],
    }),

    // ── Tasks ─────────────────────────────────────────────────────────────────
    listCaseTasks: build.query<Paginated<CaseTask>, CaseNestedListParams>({
      query: ({ caseId, page = 1, page_size = CASE_NESTED_LIST_PAGE_SIZE }) => ({
        url: `/cases/cases/${caseId}/tasks/`,
        params: { page, page_size },
      }),
      transformResponse: (response, _meta, arg) =>
        normalizeCaseNestedList<CaseTask>(response, arg),
      providesTags: (result, _e, { caseId }) =>
        result
          ? [
              ...result.results.map((t) => ({ type: "CaseTask" as const, id: t.id })),
              { type: "CaseTask", id: caseId },
            ]
          : [{ type: "CaseTask", id: caseId }],
    }),

    listAllTasks: build.query<Paginated<CaseTask>, ListAllTasksParams>({
      query: ({
        page = 1,
        page_size = BOARD_TASK_PAGE_SIZE,
        status,
        search,
        assigned_to_id,
        overdue,
        linked,
        ordering,
        cacheBust: _cacheBust,
      }) => {
        const params: Record<string, string | number> = { page, page_size };
        if (status) params.status = status;
        if (search) params.search = search;
        if (assigned_to_id != null) params.assigned_to_id = assigned_to_id;
        if (overdue) params.overdue = overdue;
        if (linked) params.linked = linked;
        if (ordering) params.ordering = ordering;
        return { url: "/cases/tasks/", params };
      },
      transformResponse: (response, _meta, arg) =>
        normalizeBoardTaskList(response, arg),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { cacheBust, ...rest } = queryArgs;
        return `${endpointName}(${JSON.stringify({
          ...rest,
          cacheBust: cacheBust ?? 0,
        })})`;
      },
      providesTags: (result) => [
        { type: "CaseTask", id: "BOARD" },
        ...(result?.results.map((t) => ({ type: "CaseTask" as const, id: t.id })) ??
          []),
      ],
    }),

    createStandaloneTask: build.mutation<CaseTask, Partial<CaseTask> | FormData>({
      query: (data) => ({
        url: "/cases/tasks/",
        method: "POST",
        body: data,
      }),
      transformResponse: (r: CaseTask | null | undefined) => {
        if (r && typeof r === "object" && typeof r.id === "number") return r;
        throw new Error("Task create returned an unexpected response.");
      },
      invalidatesTags: (result) => [
        { type: "CaseTask", id: "BOARD" },
        ...(result?.id
          ? [{ type: "CaseTaskAttachment" as const, id: result.id }]
          : []),
      ],
    }),

    listTaskAttachments: build.query<CaseTaskAttachment[], number>({
      query: (taskId) => `/cases/tasks/${taskId}/attachments/`,
      transformResponse: (
        r: CaseTaskAttachment[] | { results?: CaseTaskAttachment[] },
      ) => (Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : []),
      providesTags: (_r, _e, taskId) => [
        { type: "CaseTaskAttachment", id: taskId },
      ],
    }),

    uploadTaskAttachment: build.mutation<
      CaseTaskAttachment,
      { taskId: number; file: File }
    >({
      query: ({ taskId, file }) => {
        const form = new FormData();
        form.append("file", file);
        return {
          url: `/cases/tasks/${taskId}/attachments/`,
          method: "POST",
          body: form,
        };
      },
      invalidatesTags: (_r, _e, { taskId }) => [
        { type: "CaseTaskAttachment", id: taskId },
      ],
    }),

    listTaskComments: build.query<CaseTaskComment[], number>({
      query: (taskId) => `/cases/tasks/${taskId}/comments/`,
      transformResponse: (
        r: CaseTaskComment[] | { results?: CaseTaskComment[] },
      ) => (Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : []),
      providesTags: (_r, _e, taskId) => [
        { type: "CaseTaskComment", id: taskId },
      ],
    }),

    addTaskComment: build.mutation<
      CaseTaskComment,
      { taskId: number; body: string }
    >({
      query: ({ taskId, body }) => ({
        url: `/cases/tasks/${taskId}/comments/`,
        method: "POST",
        body: { body },
      }),
      invalidatesTags: (_r, _e, { taskId }) => [
        { type: "CaseTaskComment", id: taskId },
      ],
    }),

    listTaskActivities: build.query<CaseTaskActivity[], number>({
      query: (taskId) => `/cases/tasks/${taskId}/activities/`,
      transformResponse: (
        r: CaseTaskActivity[] | { results?: CaseTaskActivity[] },
      ) => (Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : []),
      providesTags: (_r, _e, taskId) => [
        { type: "CaseTaskActivity", id: taskId },
      ],
    }),

    addTask: build.mutation<CaseTask, { caseId: number; data: Partial<CaseTask> }>({
      query: ({ caseId, data }) => ({
        url: `/cases/cases/${caseId}/tasks/`,
        method: "POST",
        body: { ...data, case: caseId },
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "CaseTask", id: caseId },
        { type: "CaseTask", id: "BOARD" },
        { type: "CaseTimeline", id: caseId },
      ],
    }),

    /** POST /cases/tasks/{id}/assign/ - hand a task to somebody else, or
     *  clear it with assigned_to_id: null.
     *
     *  Separate from updateTask because the permission is separate: taking a
     *  task yourself is an ordinary edit, handing one out needs `tasks.assign`.
     *  Calling this without that capability returns 403 with
     *  `tnn.tasks.assign_not_permitted`. */
    assignTask: build.mutation<
      CaseTask,
      { taskId: number; caseId?: number; assigned_to_id: number | null }
    >({
      query: ({ taskId, assigned_to_id }) => ({
        url: `/cases/tasks/${taskId}/assign/`,
        method: "POST",
        body: { assigned_to_id },
      }),
      invalidatesTags: (_r, _e, { taskId, caseId }) => [
        ...(caseId ? [{ type: "CaseTask" as const, id: caseId }] : []),
        { type: "CaseTask", id: "BOARD" },
        { type: "CaseTaskActivity", id: taskId },
        ...(caseId ? [{ type: "CaseTimeline" as const, id: caseId }] : []),
      ],
    }),

    updateTask: build.mutation<CaseTask, { taskId: number; caseId?: number; data: Partial<CaseTask> }>({
      query: ({ taskId, data }) => ({
        url: `/cases/tasks/${taskId}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { taskId, caseId }) => [
        ...(caseId ? [{ type: "CaseTask" as const, id: caseId }] : []),
        { type: "CaseTask", id: "BOARD" },
        { type: "CaseTaskActivity", id: taskId },
        ...(caseId ? [{ type: "CaseTimeline" as const, id: caseId }] : []),
      ],
    }),

    deleteTask: build.mutation<void, { taskId: number; caseId?: number }>({
      query: ({ taskId }) => ({
        url: `/cases/tasks/${taskId}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { taskId, caseId }) => [
        { type: "CaseTask", id: taskId },
        { type: "CaseTask", id: "BOARD" },
        ...(caseId ? [{ type: "CaseTask" as const, id: caseId }] : []),
        ...(caseId ? [{ type: "CaseTimeline" as const, id: caseId }] : []),
      ],
    }),

    // ── Timeline ─────────────────────────────────────────────────────────────
    getCaseTimeline: build.query<{ events: TimelineEvent[] }, number>({
      query: (caseId) => `/cases/cases/${caseId}/timeline/`,
      providesTags: (_r, _e, caseId) => [{ type: "CaseTimeline", id: caseId }],
    }),

    // ── Household ─────────────────────────────────────────────────────────────
    listHousehold: build.query<Paginated<CaseHouseholdMember>, CaseNestedListParams>({
      query: ({ caseId, page = 1, page_size = CASE_NESTED_LIST_PAGE_SIZE }) => ({
        url: `/cases/cases/${caseId}/household/`,
        params: { page, page_size },
      }),
      transformResponse: (response, _meta, arg) =>
        normalizeCaseNestedList<CaseHouseholdMember>(response, arg),
      providesTags: (result, _e, { caseId }) =>
        result
          ? [
              ...result.results.map((m) => ({
                type: "ServiceCase" as const,
                id: `household-member-${m.id}`,
              })),
              { type: "ServiceCase", id: `household-${caseId}` },
            ]
          : [{ type: "ServiceCase", id: `household-${caseId}` }],
    }),

    addHouseholdMember: build.mutation<
      CaseHouseholdMember,
      { caseId: number; data: Partial<CaseHouseholdMember> }
    >({
      query: ({ caseId, data }) => ({
        url: `/cases/cases/${caseId}/household/`,
        method: "POST",
        body: { ...data, case: caseId },
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "ServiceCase", id: `household-${caseId}` },
        { type: "ServiceCase", id: caseId },
      ],
    }),

    // Correct a member. Household composition changes and names get mistyped
    // at intake; without this the only way to fix either was to delete a row.
    updateHouseholdMember: build.mutation<
      CaseHouseholdMember,
      { memberId: number; caseId: number; data: Partial<CaseHouseholdMember> }
    >({
      query: ({ memberId, data }) => ({
        url: `/cases/household/${memberId}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "ServiceCase", id: `household-${caseId}` },
        { type: "ServiceCase", id: caseId },
      ],
    }),

    deleteHouseholdMember: build.mutation<
      void,
      { memberId: number; caseId: number }
    >({
      query: ({ memberId }) => ({
        url: `/cases/household/${memberId}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { caseId }) => [
        { type: "ServiceCase", id: `household-${caseId}` },
        { type: "ServiceCase", id: caseId },
      ],
    }),

    // ── Referral orgs ─────────────────────────────────────────────────────────
    listReferralOrgs: build.query<
      ReferralOrg[],
      { q?: string; service?: string; county?: string; is_tnn_affiliated?: boolean | string } | void
    >({
      query: (params) => {
        const raw = (params ?? {}) as Record<string, string | boolean | undefined>;
        const queryParams: Record<string, string> = {};
        for (const [key, value] of Object.entries(raw)) {
          if (value === undefined || value === "") continue;
          queryParams[key] = String(value);
        }
        return {
          url: "/cases/referral-orgs/",
          params: queryParams,
        };
      },
      // v3.8 bug report v1.6 Finding 9: the API returns a paginated
      // envelope { count, results: [...] }, not a flat array. Without
      // this transformResponse, RTK Query set `data` to the envelope
      // object, `.map()` on an object did nothing, and the page
      // showed perpetual "Loading..." with no error.
      transformResponse: (r: ReferralOrg[] | { results?: ReferralOrg[] }) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({ type: "ReferralOrg" as const, id: r.id })),
              { type: "ReferralOrg" as const, id: "LIST" },
            ]
          : [{ type: "ReferralOrg" as const, id: "LIST" }],
    }),

    getReferralOrg: build.query<ReferralOrg, number>({
      query: (id) => `/cases/referral-orgs/${id}/`,
      providesTags: (_r, _e, id) => [{ type: "ReferralOrg", id }],
    }),

    listReferralOrgReferredCases: build.query<ReferralOrgReferredCase[], number>({
      query: (id) => `/cases/referral-orgs/${id}/referred-cases/`,
      providesTags: (_r, _e, id) => [{ type: "ReferralOrg", id: `CASES-${id}` }],
    }),

    // v3.8 L2.B2 - create, update, delete for the Resource Directory.
    createReferralOrg: build.mutation<ReferralOrg, Partial<ReferralOrg>>({
      query: (body) => ({
        url: "/cases/referral-orgs/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ReferralOrg", id: "LIST" }],
    }),
    updateReferralOrg: build.mutation<ReferralOrg, { id: number; data: Partial<ReferralOrg> }>({
      query: ({ id, data }) => ({
        url: `/cases/referral-orgs/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ReferralOrg", id },
        { type: "ReferralOrg", id: "LIST" },
      ],
    }),
    deleteReferralOrg: build.mutation<void, number>({
      query: (id) => ({
        url: `/cases/referral-orgs/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "ReferralOrg", id },
        { type: "ReferralOrg", id: "LIST" },
      ],
    }),

    listAssistancePrograms: build.query<
      AssistanceProgram[],
      { page_size?: number; include_inactive?: boolean } | void
    >({
      query: (params) => ({
        url: "/cases/assistance-programs/",
        params: { page_size: params?.page_size ?? 500 },
      }),
      transformResponse: (
        r: { results?: AssistanceProgram[] },
        _meta,
        arg,
      ) => {
        const rows = Array.isArray(r?.results) ? r.results : [];
        if (arg && typeof arg === "object" && arg.include_inactive) return rows;
        return rows.filter((row) => row.is_active !== false);
      },
      providesTags: [{ type: "AssistanceProgram", id: "LIST" }],
    }),
    createAssistanceProgram: build.mutation<
      AssistanceProgram,
      {
        name: string;
        category?: string;
        funder?: string;
        description?: string;
        is_active?: boolean;
      }
    >({
      query: (body) => ({
        url: "/cases/assistance-programs/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "AssistanceProgram", id: "LIST" }],
    }),
    updateAssistanceProgram: build.mutation<
      AssistanceProgram,
      {
        id: number;
        data: Partial<
          Pick<
            AssistanceProgram,
            "name" | "category" | "funder" | "description" | "is_active"
          >
        >;
      }
    >({
      query: ({ id, data }) => ({
        url: `/cases/assistance-programs/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [{ type: "AssistanceProgram", id: "LIST" }],
    }),
    deactivateAssistanceProgram: build.mutation<AssistanceProgram, number>({
      query: (id) => ({
        url: `/cases/assistance-programs/${id}/`,
        method: "PATCH",
        body: { is_active: false },
      }),
      invalidatesTags: [{ type: "AssistanceProgram", id: "LIST" }],
    }),
    deleteAssistanceProgram: build.mutation<void, number>({
      query: (id) => ({
        url: `/cases/assistance-programs/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "AssistanceProgram", id: "LIST" }],
    }),

    // ── Dashboard ─────────────────────────────────────────────────────────────
    getCaseDashboard: build.query<CaseDashboard, void>({
      query: () => "/cases/dashboard/",
      providesTags: [{ type: "CaseDashboard", id: "SUMMARY" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListCasesQuery,
  useGetCaseQuery,
  useGetCaseReferralBriefQuery,
  useCreateCaseMutation,
  useUpdateCaseMutation,
  useCloseCaseMutation,
  useListCampaignActionsQuery,
  useAddCampaignActionMutation,
  useListCaseNotesQuery,
  useAddCaseNoteMutation,
  useListDocumentsQuery,
  useAddDocumentMutation,
  useUploadDocumentFileMutation,
  useUpdateDocumentStatusMutation,
  useListDocumentChecklistsQuery,
  useApplyDocumentChecklistMutation,
  useListReferralsQuery,
  useAddReferralMutation,
  useUpdateReferralStatusMutation,
  useGetReferralPortalLinkQuery,
  useSendReferralBriefMutation,
  useListServicesQuery,
  useAddServiceMutation,
  useApproveServiceMutation,
  useRejectServiceMutation,
  useListCaseTasksQuery,
  useListAllTasksQuery,
  useCreateStandaloneTaskMutation,
  useListTaskAttachmentsQuery,
  useUploadTaskAttachmentMutation,
  useListTaskCommentsQuery,
  useAddTaskCommentMutation,
  useListTaskActivitiesQuery,
  useAddTaskMutation,
  useAssignTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetCaseTimelineQuery,
  useListHouseholdQuery,
  useAddHouseholdMemberMutation,
  useUpdateHouseholdMemberMutation,
  useDeleteHouseholdMemberMutation,
  useListReferralOrgsQuery,
  useGetReferralOrgQuery,
  useListReferralOrgReferredCasesQuery,
  useCreateReferralOrgMutation,
  useUpdateReferralOrgMutation,
  useDeleteReferralOrgMutation,
  useListAssistanceProgramsQuery,
  useCreateAssistanceProgramMutation,
  useUpdateAssistanceProgramMutation,
  useDeactivateAssistanceProgramMutation,
  useDeleteAssistanceProgramMutation,
  useGetCaseDashboardQuery,
} = casesApi;
